import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppMenuFilter from '@nxsys/components/AppMenuFilter';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { reportActions } from '@nxsys/store/slices/report/reducer/reportSlice';
import { GetEmploymentIntermediaryListParams } from '@nxsys/store/slices/report/types';
import { Icons } from '@nxsys/types/icons';
import { showMessage } from '@nxsys/utils/message';
import { debounce, includes, toNumber } from 'lodash';
import EmploymentIntermediaryFilterModal, {
  EmploymentIntermediaryFilterValues,
} from '../components/EmploymentIntermediaryFilterModal';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getEmploymentIntermediaryColumns } from './columns';
import moment from 'moment';
import { MASTER_TEMPLATE_FILE_PATH } from '@nxsys/constants/templateFiles';
import { IEmploymentIntermediary } from '@nxsys/types/models/employee';
import { escapeCSV } from '@nxsys/utils';
import { UMBRELLA_PAYE_HYBRID } from '@nxsys/constants/AppConst';
import {
  renderCategories,
  addTwoStringNumber,
} from './employmentIntermediaryUtils';


const formatGender = {
  MALE: 'M',
  FEMALE: 'F',
  OTHER: 'O',
};

const EmploymentIntermediaryReport: React.FC = () => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const { isAgency } = useSelector((state: AppState) => state.appStore);

  const dispatch = useDispatch();
  const { employmentIntermediaryList } = useSelector(
    (state: AppState) => state.reportStore,
  );
  const { list, pagination, loading, queryParams } = employmentIntermediaryList;

  const [selectedRows, setSelectedRows] = useState<IEmploymentIntermediary[]>(
    [],
  );
  const [isOpenFilterModal, setIsOpenFilterModal] = useState(false);

  const handleGoBack = useCallback(() => navigate('/reports'), [navigate]);

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        {!isAgency && <AppBackButton onClick={handleGoBack} />}
        <AppPageTitle
          prefix={messages['common.employmentIntermediaryReport']}
          separator=""
          title=""
        />
      </div>
    );
  }, [messages, handleGoBack]);

  const renderRightContent = () => {
    return (
      <div className="flex items-center gap-x-4">
        <AppButton
          variant="outlined"
          type="primary"
          name={messages['common.export']}
          leftIcon={<Icons.IconArrowExport />}
          onClick={handleExportSelected}
        />
      </div>
    );
  };

  const handleFetchData = useCallback(
    (params: GetEmploymentIntermediaryListParams) => {
      const newParams = {
        ...params,
      };

      dispatch(
        isAgency
          ? reportActions.getEmploymentIntermediaryInAgencyPortalList(newParams)
          : reportActions.getEmploymentIntermediaryList(newParams),
      );
    },
    [dispatch, isAgency],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const handleFilter = (values: EmploymentIntermediaryFilterValues) => {
    const params: GetEmploymentIntermediaryListParams = {
      ...queryParams,
      quarter: values.quarter?.value
        ? toNumber(values.quarter.value)
        : undefined,
      year: values.year ? moment(values.year).year() : undefined,
      agencyId: values.agency?.value,
    };
    handleFetchData(params);
  };

  const handleExport = async (dataExport: IEmploymentIntermediary[]) => {
    // Fetch template from public folder
    let templateText = '';
    try {
      const res = await fetch(MASTER_TEMPLATE_FILE_PATH.employmentIntermediary);
      templateText = await res.text();
    } catch (e) {
      console.error(e);
      return;
    }
    try {
      // Split template into lines
      const templateLines = templateText.split(/\r?\n/);
      // Find the first empty row (usually after agency info)
      let insertIndex = templateLines.findIndex(
        (line) => line.replace(/,/g, '').trim() === '',
      );
      if (insertIndex === -1) insertIndex = 6; // fallback if not found
      // Build agency info rows
      const headItem = dataExport?.length ? dataExport[0] : null;
      const headerAgencyValue = {
        agencyName: headItem?.agency_name || '',
        agency_address1: headItem?.agency_address?.length
          ? headItem?.agency_address[0] || ''
          : '',
        agency_address2: headItem?.agency_address?.length
          ? headItem?.agency_address[1] || ''
          : '',
        agency_address3: headItem?.agency_address?.length
          ? headItem?.agency_address[2] || ''
          : '',
        agency_address4: headItem?.agency_address?.length
          ? headItem?.agency_address[3] || ''
          : '',
        agency_postCode: headItem?.agency_postCode || '',
      };
      const agencyRow = [
        [
          'Employment intermediary name (Must be between 1 and 120 characters.)',
          headerAgencyValue?.agencyName,
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
        ],
        [
          'Employment intermediary address line 1 (Must be between 1 and 35 characters.)',
          headerAgencyValue?.agency_address1,
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
        ],
        [
          'Employment intermediary address line 2 (Must be between 1 and 35 characters.)',
          headerAgencyValue?.agency_address2,
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
        ],
        [
          '"Employment intermediary address line 3 (Optional, between 1 and 35 characters.)"',
          headerAgencyValue?.agency_address3,
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
        ],
        [
          '"Employment intermediary address line 4 (Optional, between 1 and 35 characters.)"',
          headerAgencyValue?.agency_address4,
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
        ],
        [
          'Employment intermediary postcode (Must be between 1 and 10 characters.)',
          headerAgencyValue?.agency_postCode,
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
        ],
      ];
      /**
       * Build data rows for HMRC Employment Intermediary Report (EIR) CSV.
       *
       * Business Type Categories (HMRC classification):
       *   A = Sole Trader, B = Partnership, C = Company
       *   D = Limited Liability Partnership, E = Other
       *   F = Employee (Umbrella/PAYE/Hybrid workers)
       *
       * Key rules:
       * - Umbrella/PAYE/Hybrid workers always get category 'F' (Employee)
       * - Self-employed (categories A-E) use their actual business type
       * - UTR number only applies to self-employed categories A/B/C
       * - Gross payment includes VAT if the worker is VAT-registered
       * - Company details only shown for Umbrella/PAYE/Hybrid or self-employed (A-E)
       */
      const dataRows = dataExport.map((item) => [
        // Personal details: forename, middle name and surname are separate HMRC columns
        item?.forename?.trim() || '',
        // Worker middle name: not stored by the system; column must stay per HMRC template
        '',
        item?.surname?.trim() || '',
        item?.DOB ? moment(item?.DOB)?.format('DD/MM/YYYY') : '',
        item?.gender
          ? formatGender[item?.gender as keyof typeof formatGender]
          : '',
        item?.NINumber || '',
        // Address lines 1-4
        item?.address?.length ? escapeCSV(item?.address[0]) || '' : '',
        item?.address?.length ? escapeCSV(item?.address[1]) || '' : '',
        item?.address?.length ? escapeCSV(item?.address[2]) || '' : '',
        item?.address?.length ? escapeCSV(item?.address[3]) || '' : '',
        item?.postCode || '',
        // Business type category: 'F' for employed workers, else derived from business type
        includes(UMBRELLA_PAYE_HYBRID, item?.paymentModel)
          ? 'F'
          : item?.businessType
            ? renderCategories(item?.businessType)
            : '',
        // UTR number: only for self-employed categories A/B/C (Sole Trader, Partnership, Company)
        item?.UTRNo &&
        item?.businessType &&
        includes(['A', 'B', 'C'], renderCategories(item?.businessType))
          ? item?.UTRNo
          : '',
        // Employment dates
        item?.startDate
          ? moment(item?.startDate).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        item?.dateLeft
          ? moment(item?.dateLeft).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        // Gross payment: includes VAT if VAT-registered; applies to employed or self-employed (A-E)
        includes(UMBRELLA_PAYE_HYBRID, item?.paymentModel)
          ? item.VATRegNo
            ? addTwoStringNumber(item.totalGross, item.totalVat).toFixed(2)
            : item?.totalGross
          : item?.businessType &&
              includes(
                ['A', 'B', 'C', 'D', 'E'],
                renderCategories(item?.businessType),
              )
            ? item.VATRegNo
              ? addTwoStringNumber(item.totalGross, item.totalVat).toFixed(2)
              : item?.totalGross
            : '',
        'GBP',
        // VAT registered flag
        item?.VATRegNo ? 'Y' : 'N',
        // Company details: only populated for employed or self-employed (A-E) workers
        includes(UMBRELLA_PAYE_HYBRID, item?.paymentModel)
          ? item?.company_companyName
          : item?.businessType &&
              includes(
                ['A', 'B', 'C', 'D', 'E'],
                renderCategories(item?.businessType),
              )
            ? item?.company_companyName
            : '',
        // Company address lines 1-4
        !item?.company_address?.length
          ? ''
          : includes(UMBRELLA_PAYE_HYBRID, item?.paymentModel)
            ? escapeCSV(item?.company_address[0]) || ''
            : item?.businessType &&
                includes(
                  ['A', 'B', 'C', 'D', 'E'],
                  renderCategories(item?.businessType),
                )
              ? escapeCSV(item?.company_address[0]) || ''
              : '',
        !item?.company_address?.length
          ? ''
          : includes(UMBRELLA_PAYE_HYBRID, item?.paymentModel)
            ? escapeCSV(item?.company_address[1]) || ''
            : item?.businessType &&
                includes(
                  ['A', 'B', 'C', 'D', 'E'],
                  renderCategories(item?.businessType),
                )
              ? escapeCSV(item?.company_address[1]) || ''
              : '',
        !item?.company_address?.length
          ? ''
          : includes(UMBRELLA_PAYE_HYBRID, item?.paymentModel)
            ? escapeCSV(item?.company_address[2]) || ''
            : item?.businessType &&
                includes(
                  ['A', 'B', 'C', 'D', 'E'],
                  renderCategories(item?.businessType),
                )
              ? escapeCSV(item?.company_address[2]) || ''
              : '',
        !item?.company_address?.length
          ? ''
          : includes(UMBRELLA_PAYE_HYBRID, item?.paymentModel)
            ? escapeCSV(item?.company_address[3]) || ''
            : item?.businessType &&
                includes(
                  ['A', 'B', 'C', 'D', 'E'],
                  renderCategories(item?.businessType),
                )
              ? escapeCSV(item?.company_address[3]) || ''
              : '',
        // Company postcode
        includes(UMBRELLA_PAYE_HYBRID, item?.paymentModel)
          ? item?.company_postCode || ''
          : item?.businessType &&
              includes(
                ['A', 'B', 'C', 'D', 'E'],
                renderCategories(item?.businessType),
              )
            ? item?.company_postCode || ''
            : '',
        // Company registration number
        includes(UMBRELLA_PAYE_HYBRID, item?.paymentModel)
          ? item?.company_companyNo || ''
          : item?.businessType &&
              includes(
                ['A', 'B', 'C', 'D', 'E'],
                renderCategories(item?.businessType),
              )
            ? item?.company_companyNo || ''
            : '',
      ]);
      // Build final csv content
      // Replace agency info rows in template
      const newLines = [
        ...agencyRow.map((row) => row.join(',')),
        templateLines[6], // empty row
        templateLines[7], // main header row
        ...dataRows.map((row) => row.join(',')),
      ];
      const csvContent = newLines.join('\r\n');
      // Tạo tên file có ngày hiện tại
      const fileName = `Employment_Intermediary_Report-${moment(new Date())
        .tz('Europe/London')
        .format('DD/MM/YYYY')}`;
      // Download as file
      const blob = new Blob([csvContent], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed:', e);
      console.error(e);
    }
  };

  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExport(selectedRows);
  };

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
    });
  }, []);

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.quarter ||
    !!queryParams?.year ||
    !!queryParams?.agencyId;

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
    >
      <AppCard>
        <AppsHeader>
          <div className="flex justify-between flex-wrap w-full gap-4">
            <div className="flex flex-wrap gap-4">
              <AppInputSearch onChange={handleSearch} />
              <AppMenuFilter
                options={[]}
                onChange={() => {}}
                onClick={() => setIsOpenFilterModal(true)}
              />
            </div>
          </div>
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            className="table-header-wrap"
            rowKey="id_index"
            data={
              list?.map((item, index) => ({ ...item, id_index: index })) ?? []
            }
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 13000, y: 700 }}
            columns={getEmploymentIntermediaryColumns({
              messages,
            })}
            onChangePagination={onChangePagination}
            rowSelection={{
              type: 'checkbox',
              onChange: (_: React.Key[], selectedRows: any[]) => {
                setSelectedRows(selectedRows);
              },
            }}
            titleEmpty={
              messages['report.employmentIntermediary.needFilterToViewData']
            }
            noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
          />
        </AppsContent>
      </AppCard>
      <EmploymentIntermediaryFilterModal
        isOpen={isOpenFilterModal}
        toggle={() => setIsOpenFilterModal(false)}
        onFilter={handleFilter}
      />
    </AppsContainer>
  );
};

export default EmploymentIntermediaryReport;
